import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * Reusable SEO component.
 *
 * Drop-in <PageSeo> at the top of any page:
 *
 *   <PageSeo
 *     title={{ fr: "Boutique - Matériel géotechnique", en: "Shop" }}
 *     description={{ fr: "...", en: "..." }}
 *     path="/shop"
 *     image="/og-image-shop.jpg"
 *   />
 *
 * Handles automatically:
 *   - <title> + meta description (per language)
 *   - canonical URL
 *   - Open Graph tags
 *   - Twitter Card tags
 *   - <html lang="..."> sync
 *   - Optional JSON-LD structured data
 *   - Optional noindex (for thin pages, internal pages, etc.)
 *
 * Image paths can be relative (/og-image.jpg) or absolute. Relative paths
 * are automatically prefixed with the production domain so social
 * crawlers (LinkedIn, Twitter, WhatsApp) can fetch them.
 */

const SITE_URL = 'https://geots.consulting';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

function toAbsoluteUrl(path) {
  if (!path) return DEFAULT_OG_IMAGE;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${SITE_URL}${path}`;
  return `${SITE_URL}/${path}`;
}

export default function PageSeo({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'fr';
  const isFr = lang === 'fr';

  // Accept either a string or { fr, en } object.
  const resolvedTitle = typeof title === 'string' ? title : (isFr ? title.fr : title.en);
  const resolvedDesc = typeof description === 'string' ? description : (isFr ? description.fr : description.en);

  const canonicalUrl = `${SITE_URL}${path}`;
  const absoluteImage = toAbsoluteUrl(image);

  // Append site name to title if not already there
  const fullTitle = resolvedTitle.includes('GEOTS')
    ? resolvedTitle
    : `${resolvedTitle} | GEOTS Consulting`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDesc} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDesc} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="GEOTS Consulting" />
      <meta property="og:locale" content={isFr ? 'fr_FR' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDesc} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Optional structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Helper to build a BreadcrumbList JSON-LD.
 * Usage:
 *   const breadcrumbs = buildBreadcrumbs([
 *     { name: 'Accueil', path: '/' },
 *     { name: 'Boutique', path: '/shop' },
 *   ]);
 *   <PageSeo ... jsonLd={breadcrumbs} />
 */
export function buildBreadcrumbs(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

/**
 * Helper to build an ItemList JSON-LD (for catalog/listing pages).
 */
export function buildItemList(items, type = 'Product') {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': type,
        name: item.name,
        url: `${SITE_URL}${item.path}`,
        image: item.image,
      },
    })),
  };
}
